import React, { useEffect, useState } from 'react';
import Overlay from '@ynap/overlay';
import BEMHelper from '@ynap/bem-helper';
import { useContentByComponentKey } from '@ynap/content-hook';
import Markdown from 'markdown-to-jsx';
import type {
    ContentByComponentKeyResponse,
    ContentByComponentKeyProps,
    NormalizedContent,
    ManufacturerDetailsProps,
    ManufacturerDetailsContentProps,
} from './types/ManufacturerDetails';

const bem = new BEMHelper('EditorialAccordion');

export const getComponentKey = (seoURLKeyword: string): string | null => {
    const designerName = seoURLKeyword && seoURLKeyword.split('/').pop();
    return designerName ? `manufacturer-${designerName}` : null;
};

export const getContentByComponentKey = (contentByComponentKey: ContentByComponentKeyProps): NormalizedContent => {
    if (!contentByComponentKey) {
        return {};
    }
    return {
        content: contentByComponentKey?.content?.[0]?.teaser?.teaserText,
    };
};

function ManufacturerDetails({ messages, designerIdentifier }: ManufacturerDetailsProps) {
    const [open, setOpen] = useState<boolean>(false);
    const [componentKey, setComponentKey] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setComponentKey(getComponentKey(designerIdentifier));
        } else {
            setComponentKey(null);
        }
    }, [open, designerIdentifier]);

    const closeOverlay = () => setOpen(false);

    return (
        <>
            <div className={bem('manufacturerDetails')} onClick={() => setOpen(!open)}>
                {messages?.manufacturerDetails?.button()}
            </div>
            {open && componentKey && (
                <Overlay
                    onClose={closeOverlay}
                    handleBodyClick={closeOverlay}
                    className={{ container: bem('overlay'), content: bem('content'), button: bem('close') }}
                    title={messages?.manufacturerDetails?.title()}
                >
                    <ManufacturerDetailsContent componentKey={componentKey} messages={messages} />
                </Overlay>
            )}
        </>
    );
}

function ManufacturerDetailsContent({ componentKey, messages }: ManufacturerDetailsContentProps) {
    const { content, loading }: ContentByComponentKeyResponse = useContentByComponentKey({
        componentKey,
        normalize: getContentByComponentKey,
    });

    let teaserText = content?.content;
    if (teaserText) {
        // if the reponse of coremedia has a leading newline, remove it
        teaserText = teaserText.replace(/^\n\n/, '');
    }

    return (
        <div className={bem('manufacturerDetailsText')}>
            {loading ? null : teaserText ? (
                <Markdown options={{ forceInline: true }} className={bem('manufacturerDetailsTextBody')} data-testid="manufacturer-body">
                    {teaserText}
                </Markdown>
            ) : (
                messages?.manufacturerDetails?.content()
            )}
        </div>
    );
}

export default ManufacturerDetails;
